import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { EnterpriseAnalyticsSummary } from 'models/EnterprisesAnalytics';
import AnalyticsCard from '../AnalyticsCard';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import { Loader } from 'ncoded-component-library';
import {
  mapValueToMoneyFormat,
  transformPriceNumberToCurrencyString,
} from 'utils';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import { Link } from 'react-router-dom';
import ArrowrightIcon from 'icons/Arrowright.icon';

import './SummaryCard.styles.scss';
import './SummaryCard.styles.responsive.scss';

type Item = {
  key: string;
  value: string | number;
  link?: string;
  popup?: string;
  diff?: number;
};

const transformPrice = (value: number) => {
  const roundedValue = Math.round(value);

  return roundedValue > 99000
    ? mapValueToMoneyFormat(roundedValue)
    : transformPriceNumberToCurrencyString(roundedValue);
};

const prepareData = (data: EnterpriseAnalyticsSummary): Item[] => {
  const {
    liveDealsCount,
    dealsWithListingCount,
    activeTeamMembers,
    brokersInvitedCount,
    totalProjectedSaleValue,
    totalProjectedSuccessFee,
    totalProjectedFranchisorFee,
    teamMembersWithoutDeal,
  } = data;

  return [
    {
      key: 'entepriseNameDeals',
      value: liveDealsCount,
    },
    {
      key: 'entepriseNameListings',
      value: dealsWithListingCount,
    },
    {
      key: 'totalMarketCap',
      popup: 'totalMarketCapPopup',
      value: transformPrice(totalProjectedSaleValue),
    },
    {
      key: 'activeTeamMembers',
      popup: 'activeTeamMembersPopup',
      value: activeTeamMembers,
    },
    {
      key: 'pendingAcceptance',
      popup: 'pendingAcceptancePopup',
      value: brokersInvitedCount,
    },
    {
      key: 'teamMembersWithoutDeal',
      popup: 'teamMembersWithoutDealPopup',
      value: teamMembersWithoutDeal,
    },
    {
      key: 'commissionPipeline',
      value: transformPrice(totalProjectedSuccessFee),
    },
    {
      key: 'projectedFranchisorFees',
      value: transformPrice(totalProjectedFranchisorFee),
    },
  ];
};

type SummaryCardProps = {
  className?: string;
};

const SummaryCard: React.FC<SummaryCardProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<Item[]>();

  const { enterprise } = useContext(EnterpriseContext);

  const classes = classNames('summary-card', className);

  const { t } = useTranslation();

  const RenderItem = useCallback(
    (item: Item) => {
      const { key, value, popup, link } = item;

      return (
        <div className="summary-card__content__item" key={key}>
          <p className="summary-card__content__item__key">
            {t(key, { entepriseName: enterprise?.brandName })}

            {popup && (
              <ResolvePopup onHover popupContent={t(popup)}>
                <InformationcircleIcon />
              </ResolvePopup>
            )}
          </p>
          <p className="summary-card__content__item__value">
            {value}

            {link && (
              <Link to={link}>
                {t('view')} <ArrowrightIcon />
              </Link>
            )}
          </p>
        </div>
      );
    },
    [enterprise, t],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.enterpriseAnalytics.getSummary(
          enterprise?._id,
        );
        setData(prepareData(data));
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [enterprise?._id]);

  return (
    <AnalyticsCard className={classes}>
      <div className="summary-card__content">
        {isLoading ? (
          <Loader />
        ) : !data?.length ? null : (
          data?.map((el) => RenderItem(el))
        )}
      </div>
    </AnalyticsCard>
  );
};

export default SummaryCard;
